import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { STORAGE_PATHS } from '@canvas/firebase-config/config';
import { getClientStorage } from '@canvas/firebase-config/client';
import { downloadBlob } from '@/lib/pdf/pdfExport';

export interface PdfEmailDraft {
  to: string;
  subject: string;
  message: string;
}

export type PdfEmailSendResult = 'shared' | 'mailto';

function sanitizeFilename(name: string): string {
  const base = name.replace(/[^\w.\-]+/g, '_').replace(/^\.+/, '');
  return base.toLowerCase().endsWith('.pdf') ? base : `${base}.pdf`;
}

export function parseEmailList(raw: string): string[] {
  return raw
    .split(/[,;\s]+/)
    .map((e) => e.trim())
    .filter(Boolean);
}

export async function uploadPdfForEmail(
  userId: string,
  blob: Blob,
  filename: string
): Promise<string> {
  const storage = getClientStorage();
  const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const safe = sanitizeFilename(filename);
  const storagePath = `${STORAGE_PATHS.tempUploads(userId)}/mail-${id}-${safe}`;
  const fileRef = ref(storage, storagePath);
  await uploadBytes(fileRef, blob, { contentType: 'application/pdf' });
  return getDownloadURL(fileRef);
}

function canSharePdfFile(file: File): boolean {
  return (
    typeof navigator !== 'undefined' &&
    typeof navigator.share === 'function' &&
    typeof navigator.canShare === 'function' &&
    navigator.canShare({ files: [file] })
  );
}

function openMailto(draft: PdfEmailDraft, link?: string): void {
  const body = [draft.message.trim(), link ? `\n${link}` : '']
    .join('\n')
    .trim();
  const to = draft.to.replace(/\s+/g, '');
  const params: string[] = [];
  if (draft.subject) params.push(`subject=${encodeURIComponent(draft.subject)}`);
  if (body) params.push(`body=${encodeURIComponent(body)}`);
  const href = `mailto:${to}${params.length ? `?${params.join('&')}` : ''}`;
  const a = document.createElement('a');
  a.href = href;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/**
 * Share the exported PDF via native share sheet when possible;
 * otherwise upload a temp copy and open the user's mail client with a link.
 */
export async function sendPdfToEmail(options: {
  userId: string;
  blob: Blob;
  filename: string;
  draft: PdfEmailDraft;
}): Promise<PdfEmailSendResult> {
  const filename = sanitizeFilename(options.filename);
  const file = new File([options.blob], filename, { type: 'application/pdf' });

  if (canSharePdfFile(file)) {
    try {
      await navigator.share({
        files: [file],
        title: options.draft.subject || filename,
        text: options.draft.message || filename,
      });
      return 'shared';
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') throw err;
      // Fall through to mailto + link
    }
  }

  let link: string | undefined;
  try {
    link = await uploadPdfForEmail(options.userId, options.blob, filename);
  } catch (err) {
    console.warn('PDF email upload failed; opening mailto without link', err);
    downloadBlob(options.blob, filename);
  }

  const message = link
    ? options.draft.message
    : [options.draft.message.trim(), '', 'Attach the downloaded PDF to this email.']
        .filter(Boolean)
        .join('\n');

  openMailto({ ...options.draft, message }, link);
  return 'mailto';
}
